import { HttpClient } from '@angular/common/http';
import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  AdminTransactionCreatePayload,
  StaffObservationEntry,
  Transaction,
  TransactionDetail,
  TransactionListResponse,
} from '../models/transaction';
import { UserAlert } from '../models/user-alert';

export const TRANSACTION_TYPE_DEFINITIONS: { code: string; label: string }[] = [
  { code: 'DIAMOND_PURCHASE', label: 'Compra de diamantes' },
  { code: 'COIN_PURCHASE', label: 'Compra de moedas' },
  { code: 'CASH_OUT', label: 'Saque' },
  { code: 'LEAGUE_ENTRY', label: 'Participação da Liga' },
  { code: 'LEAGUE_PRIZE', label: 'Premiação da Liga' },
  { code: 'LEAGUE_CREATOR_EARNING', label: 'Ganhos para o criador da Liga' },
  { code: 'LEAGUE_REFUND', label: 'Reembolso de participação da Liga' },
  { code: 'LEAGUE_BOOST_REFUND', label: 'Reembolso de boost' },
  { code: 'BOOST_PURCHASE', label: 'Compra de boost' },
  { code: 'CURRENCY_CONVERSION', label: 'Conversão de diamantes' },
  { code: 'BATTLE_COST', label: 'Custo de batalha' },
  { code: 'BATTLE_PRIZE', label: 'Premiação de batalha' },
  { code: 'BATTLE_REFUND', label: 'Reembolso de batalha' },
  { code: 'CASHBACK', label: 'Cashback' },
  { code: 'HERO_PURCHASE', label: 'Compra de hero' },
  { code: 'BATTLE_PASS_REWARD', label: 'Premiação do passe de batalha' },
  { code: 'BATTLE_PASS_PURCHASE', label: 'Compra do passe de batalha' },
  { code: 'MISUSE', label: 'Uso indevido da plataforma' },
  { code: 'RANKING_PRIZE', label: 'Premiação do ranking' },
  { code: 'REFERRAL_GAIN', label: 'Ganho por indicação' },
];

export function buildTransactionTypeFilterOptions(): { label: string; value: string }[] {
  const sorted = [...TRANSACTION_TYPE_DEFINITIONS].sort((a, b) =>
    a.label.localeCompare(b.label, 'pt-BR')
  );
  return [
    { label: 'Todos', value: '' },
    ...sorted.map(({ code, label }) => ({ label, value: code })),
  ];
}

export function resolveTransactionTypeLabel(
  t: Pick<Transaction, 'transaction_type' | 'type_display' | 'type'>
): string {
  const rawCode = String(t.transaction_type || t.type || '').trim();
  if (rawCode) {
    const row = TRANSACTION_TYPE_DEFINITIONS.find(
      (d) => d.code.toUpperCase() === rawCode.toUpperCase()
    );
    if (row) {
      return row.label;
    }
  }
  const display = String(t.type_display || '').trim();
  if (display) {
    return display;
  }
  if (rawCode) {
    return rawCode;
  }
  return '-';
}

const WORKFLOW_STAGE_LABELS: Record<string, string> = {
  REQUESTED: 'Solicitado',
  IN_REVIEW: 'Em revisão',
  APPROVED: 'Aprovado (saque)',
  PAID: 'Pago',
  REJECTED: 'Rejeitado (saque)',
};

export function resolveWorkflowStageLabel(stage?: string | null): string {
  const s = String(stage ?? '').trim();
  if (!s) {
    return '—';
  }
  return WORKFLOW_STAGE_LABELS[s] ?? s;
}

export interface TransactionListFilters {
  status?: string;
  search?: string;
  page?: number;
  page_size?: number;
  transaction_type?: string;
  trusted?: '' | '1' | '0';
  has_open_risk?: '' | '1' | '0';
  start_date?: string;
  end_date?: string;
}

@Injectable({
  providedIn: 'root',
})
export class TransactionService {
  constructor(private http: HttpClient) { }

  private adminTransactionBase(id: string | number): string {
    return `${environment.apiUrl}/admin/transactions/${encodeURIComponent(String(id))}/`;
  }

  setAccountTrusted(
    accountId: number,
    isActive: boolean,
    revokeReason?: string | null
  ): Observable<void> {
    const body: { is_active: boolean; reason?: string } = { is_active: isActive };
    if (!isActive && revokeReason?.trim()) {
      body.reason = revokeReason.trim();
    }
    return this.http.post<void>(
      `${environment.apiUrl}/admin/accounts/${accountId}/trusted_override/`,
      body
    );
  }

  getTransaction(id: string | number): Observable<TransactionDetail> {
    return this.http
      .get<Record<string, unknown>>(`${this.adminTransactionBase(id)}`)
      .pipe(map((raw) => this.normalizeTransactionDetail(raw)));
  }

  updateWorkflowStage(id: string | number, workflowStage: string): Observable<void> {
    return this.http.patch<void>(`${this.adminTransactionBase(id)}`, {
      workflow_stage: workflowStage,
    });
  }

  postStaffObservation(
    transactionId: string | number,
    text: string
  ): Observable<StaffObservationEntry> {
    const trimmed = text.trim();
    if (!trimmed) {
      return throwError(() => ({ status: 400, message: 'Texto vazio.' }));
    }
    return this.http.post<StaffObservationEntry>(
      `${this.adminTransactionBase(transactionId)}staff_observations/`,
      { text: trimmed }
    );
  }

  patchStaffObservationEntry(
    transactionId: string | number,
    entryId: string,
    text: string
  ): Observable<void> {
    const trimmed = text.trim();
    if (!trimmed) {
      return throwError(() => ({ status: 400, message: 'Texto vazio.' }));
    }
    return this.http.patch<void>(
      `${this.adminTransactionBase(transactionId)}staff_observations/${encodeURIComponent(entryId)}/`,
      { text: trimmed }
    );
  }

  patchStaffObservations(id: string | number, staffObservations: string): Observable<void> {
    const trimmed = staffObservations.trim();
    return this.http.patch<void>(`${this.adminTransactionBase(id)}`, {
      staff_observations: trimmed,
    });
  }

  approvePayment(id: string | number): Observable<void> {
    return this.http.post<void>(`${this.adminTransactionBase(id)}approve/`, {});
  }

  rejectPayment(id: string | number, reason: string): Observable<void> {
    return this.http.post<void>(`${this.adminTransactionBase(id)}reject/`, {
      reason: reason.trim(),
    });
  }

  markPaidWithReceipt(id: string | number, file: File): Observable<void> {
    const fd = new FormData();
    fd.append('pix_receipt', file, file.name);
    return this.http.post<void>(`${this.adminTransactionBase(id)}mark_paid/`, fd);
  }

  getUsersTransaction(): Observable<UserAlert[]> {
    return this.http.get<UserAlert[]>(
      `${environment.apiUrl}/accounts/user_transactions/`
    );
  }

  createTransaction(body: AdminTransactionCreatePayload): Observable<Transaction> {
    return this.http.post<Transaction>(
      `${environment.apiUrl}/admin/transactions/`,
      body
    );
  }

  listTransactions(filters?: TransactionListFilters): Observable<TransactionListResponse> {
    const params = this.buildTransactionListHttpParams(filters);
    return this.http
      .get<TransactionListResponse | unknown>(`${environment.apiUrl}/admin/transactions/`, { params })
      .pipe(map((res) => this.normalizeTransactionListResponse(res)));
  }

  private normalizeTransactionListResponse(res: unknown): TransactionListResponse {
    if (Array.isArray(res)) {
      const list = res as unknown[];
      return {
        count: list.length,
        results: list.map((row) =>
          this.normalizeListTransactionRow(row as Record<string, unknown>)
        ),
      };
    }
    const raw = res as Record<string, unknown>;
    const rawResults = raw['results'] ?? raw['data'] ?? raw['items'];
    const list: unknown[] = Array.isArray(rawResults) ? rawResults : [];
    let count: number;
    if (typeof raw['count'] === 'number' && Number.isFinite(raw['count'])) {
      count = raw['count'];
    } else if (typeof raw['total'] === 'number' && Number.isFinite(raw['total'])) {
      count = raw['total'];
    } else if (typeof raw['total_count'] === 'number' && Number.isFinite(raw['total_count'])) {
      count = raw['total_count'];
    } else {
      count = list.length;
    }
    return {
      count,
      results: list.map((row) =>
        this.normalizeListTransactionRow(row as Record<string, unknown>)
      ),
      next: (raw['next'] as string | null | undefined) ?? undefined,
      previous: (raw['previous'] as string | null | undefined) ?? undefined,
    };
  }

  exportTransactionsCsv(
    filters: Omit<TransactionListFilters, 'page' | 'page_size'>
  ): Observable<{ blob: Blob; filename?: string }> {
    const params = this.buildTransactionListHttpParams(filters as TransactionListFilters);
    return this.http
      .get(`${environment.apiUrl}/admin/transactions/export_csv/`, {
        params,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((res) => {
          let filename: string | undefined;
          const cd = res.headers.get('Content-Disposition');
          if (cd) {
            const m = /filename\*?=(?:UTF-8''|"?)([^\";]+)/i.exec(cd);
            if (m) {
              filename = decodeURIComponent(m[1].replace(/"/g, '').trim());
            }
          }
          return { blob: res.body as Blob, filename };
        })
      );
  }

  private buildTransactionListHttpParams(filters?: TransactionListFilters): HttpParams {
    let params = new HttpParams();
    if (!filters) {
      return params.append('ordering', '-created_at');
    }
    if (filters.status) {
      const status = filters.status === 'REJECTED' ? 'FAILED' : filters.status;
      params = params.append('status', status);
    }
    const userQ = filters.search?.trim();
    if (userQ) {
      if (/^\d+$/.test(userQ)) {
        params = params.append('user_id', userQ);
      } else {
        params = params.append('username', userQ);
      }
    }
    if (filters.page) {
      params = params.append('page', filters.page.toString());
    }
    if (filters.page_size) {
      params = params.append('page_size', filters.page_size.toString());
    }
    if (filters.transaction_type?.trim()) {
      params = params.append('type', filters.transaction_type.trim());
    }
    if (filters.trusted === '1') {
      params = params.append('is_trusted', 'true');
    } else if (filters.trusted === '0') {
      params = params.append('is_trusted', 'false');
    }
    if (filters.has_open_risk === '1') {
      params = params.append('has_risk', 'true');
    } else if (filters.has_open_risk === '0') {
      params = params.append('has_risk', 'false');
    }
    if (filters.start_date) {
      params = params.append('start_date', filters.start_date);
    }
    if (filters.end_date) {
      params = params.append('end_date', filters.end_date);
    }
    return params.append('ordering', '-created_at');
  }

  private cleanPersonDisplayName(raw?: string | null): string | undefined {
    const s = String(raw ?? '').trim();
    if (!s || s === 'None None') {
      return undefined;
    }
    return s;
  }

  private riskTruthy(v: unknown): boolean {
    if (v === true || v === 1) {
      return true;
    }
    if (v === false || v === 0) {
      return false;
    }
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === 'true' || s === '1' || s === 'yes';
    }
    return false;
  }

  private static readonly LIST_ROOT_MARK_RISK_KEYS = [
    'risk',
    'has_risk',
    'has_alert',
    'user_has_risk',
    'open_risk',
  ] as const;

  private static readonly LIST_NESTED_OBJECT_KEYS = ['user', 'user_data', 'account'] as const;

  private applyListRiskFromRecord(obj: Record<string, unknown>, t: Transaction, includeRootOnlySignals: boolean): void {
    const markRisk = (): void => {
      t.has_risk = true;
    };
    for (const k of TransactionService.LIST_ROOT_MARK_RISK_KEYS) {
      if (!includeRootOnlySignals && (k === 'user_has_risk' || k === 'open_risk')) {
        continue;
      }
      if (this.riskTruthy(obj[k])) {
        markRisk();
      }
    }
    if (this.riskTruthy(obj['has_open_risk'])) {
      t.has_open_risk = true;
    }
    if (includeRootOnlySignals && typeof obj['alert_count'] === 'number' && obj['alert_count'] > 0) {
      markRisk();
    }
    const alerts = obj['alerts'];
    if (Array.isArray(alerts) && alerts.length > 0) {
      markRisk();
    }
    const openAlerts = obj['open_alerts'];
    if (Array.isArray(openAlerts) && openAlerts.length > 0) {
      markRisk();
      t.has_open_risk = true;
    }
  }

  private applyListRiskFromApi(t: Transaction & Record<string, unknown>): void {
    const raw = t as Record<string, unknown>;
    this.applyListRiskFromRecord(raw, t, true);
    for (const key of TransactionService.LIST_NESTED_OBJECT_KEYS) {
      const n = raw[key];
      if (n && typeof n === 'object') {
        this.applyListRiskFromRecord(n as Record<string, unknown>, t, false);
      }
    }
  }

  private applyApiFlatFields(t: Transaction & Record<string, unknown>): void {
    const raw = t as Record<string, unknown>;
    if (t.id == null && raw['transaction_id'] != null) {
      t.id = String(raw['transaction_id']);
    }
    if (t.transaction_id == null && raw['transaction_id'] != null) {
      t.transaction_id = String(raw['transaction_id']);
    }
    if (t.account_id == null && raw['user_id'] != null) {
      const uid = Number(raw['user_id']);
      if (Number.isFinite(uid)) {
        t.account_id = uid;
      }
    }
    if (t.user_id == null && raw['user_id'] != null) {
      const uid = Number(raw['user_id']);
      if (Number.isFinite(uid)) {
        t.user_id = uid;
      }
    }
    if (t.amount == null && raw['value'] != null) {
      t.amount = Number(raw['value']);
    }
    if (t.quantity == null && raw['value'] != null) {
      t.quantity = Number(raw['value']);
    }
    if (!t.transaction_type && raw['type']) {
      t.transaction_type = String(raw['type']);
    }
    if (t.full_name == null && raw['full_name'] != null) {
      t.full_name = String(raw['full_name']);
    }
    const displayName = this.cleanPersonDisplayName(t.full_name);
    if (!t.account && t.account_id != null) {
      t.account = {
        id: t.account_id,
        email: '',
        username: displayName,
      };
    }
    if (this.riskTruthy(raw['risk'])) {
      t.has_risk = true;
    }
  }

  private normalizeListTransactionRow(raw: Record<string, unknown>): Transaction {
    const t = { ...raw } as Transaction & Record<string, unknown>;
    this.applyApiFlatFields(t);
    this.applyListRiskFromApi(t);
    this.mergeRowRiskAlerts(t);
    if (typeof t.has_open_risk !== 'boolean' && typeof t.has_risk === 'boolean') {
      t.has_open_risk = t.has_risk;
    }
    return t;
  }

  private static readonly ALERT_OBJECT_TEXT_KEYS = [
    'message',
    'description',
    'text',
    'detail',
    'title',
    'name',
    'label',
  ] as const;

  private normalizeAlertArray(items: unknown[]): string[] {
    const lines: string[] = [];
    for (const item of items) {
      if (typeof item === 'string') {
        const s = item.trim();
        if (s) {
          lines.push(s);
        }
        continue;
      }
      if (item && typeof item === 'object') {
        const o = item as Record<string, unknown>;
        for (const k of TransactionService.ALERT_OBJECT_TEXT_KEYS) {
          const t = o[k];
          if (t == null) {
            continue;
          }
          const s = String(t).trim();
          if (s) {
            lines.push(s);
            break;
          }
        }
      }
    }
    return lines;
  }

  private collectRiskAlertMessages(rec: Record<string, unknown>): string[] {
    const lines: string[] = [];
    const push = (v: unknown): void => {
      if (v == null) {
        return;
      }
      const s = String(v).trim();
      if (s) {
        lines.push(s);
      }
    };
    push(rec['risk_message']);
    push(rec['risk_reason']);
    push(rec['alert_message']);
    push(rec['risk_label']);
    push(rec['risk_type']);
    push(rec['risk_description']);
    if (Array.isArray(rec['risk_alerts'])) {
      lines.push(...this.normalizeAlertArray(rec['risk_alerts']));
    }
    if (Array.isArray(rec['alerts'])) {
      lines.push(...this.normalizeAlertArray(rec['alerts']));
    }
    if (Array.isArray(rec['open_alerts'])) {
      lines.push(...this.normalizeAlertArray(rec['open_alerts']));
    }
    return [...new Set(lines)];
  }

  private mergeUniqueAlertLines(merged: string[], incoming: string[]): void {
    for (const s of incoming) {
      const x = s?.trim();
      if (x && !merged.includes(x)) {
        merged.push(x);
      }
    }
  }

  private mergeRowRiskAlerts(t: Transaction & Record<string, unknown>): void {
    const merged: string[] = [];
    this.mergeUniqueAlertLines(merged, this.collectRiskAlertMessages(t as Record<string, unknown>));
    for (const key of ['user_data', 'user', 'account', 'cashout_data'] as const) {
      const n = (t as Record<string, unknown>)[key];
      if (n && typeof n === 'object') {
        this.mergeUniqueAlertLines(
          merged,
          this.collectRiskAlertMessages(n as Record<string, unknown>)
        );
      }
    }
    if (merged.length) {
      t.risk_alerts = merged;
    }
  }

  private mergeDetailRiskAlerts(out: TransactionDetail, raw: Record<string, unknown>): void {
    const merged: string[] = [];
    this.mergeUniqueAlertLines(merged, this.collectRiskAlertMessages(raw));
    const ud = raw['user_data'];
    if (ud && typeof ud === 'object') {
      this.mergeUniqueAlertLines(merged, this.collectRiskAlertMessages(ud as Record<string, unknown>));
    }
    const cd = raw['cashout_data'];
    if (cd && typeof cd === 'object') {
      this.mergeUniqueAlertLines(merged, this.collectRiskAlertMessages(cd as Record<string, unknown>));
    }
    if (merged.length) {
      out.risk_alerts = merged;
    }
  }

  private normalizeTransactionDetail(raw: Record<string, unknown>): TransactionDetail {
    const out = { ...raw } as TransactionDetail & Record<string, unknown>;
    this.applyApiFlatFields(out as Transaction & Record<string, unknown>);
    const ud = raw['user_data'] as Record<string, unknown> | undefined;
    const cd = raw['cashout_data'] as Record<string, unknown> | undefined;

    if (ud) {
      const id = Number(ud['id'] ?? out.account_id ?? out.account?.id ?? 0);
      out.account = {
        id,
        email: String(ud['email'] ?? out.account?.email ?? ''),
        username:
          ud['username'] != null ? String(ud['username']) : out.account?.username,
        gem_amount:
          ud['gem_amount'] != null
            ? Number(ud['gem_amount'])
            : out.account?.gem_amount,
        coin_amount:
          ud['coin_amount'] != null
            ? Number(ud['coin_amount'])
            : out.account?.coin_amount,
      };
      out.account_id = id;
      if (ud['gem_balance'] != null) {
        out.user_gem_balance = Number(ud['gem_balance']);
      }
      if (ud['coin_balance'] != null) {
        out.user_coin_balance = Number(ud['coin_balance']);
      }
      if (ud['birthdate'] != null) {
        out.birthdate = ud['birthdate'] as string | Date;
      }
      if (ud['document_number'] != null) {
        out.document_number = String(ud['document_number']);
      } else if (ud['cpf'] != null) {
        out.document_number = String(ud['cpf']);
      }
      if (this.riskTruthy(ud['risk'])) {
        out.has_risk = true;
        out.has_open_risk = true;
      }

      if (Array.isArray(ud['open_notes'])) {
        out.notes = ud['open_notes'] as { id: number; description: string; created_at: string }[];
      }
    }

    if (out.birthdate == null && raw['birthdate'] != null) {
      out.birthdate = raw['birthdate'] as string | Date;
    }
    if (out.document_number == null && raw['document_number'] != null) {
      out.document_number = String(raw['document_number']);
    } else if (out.document_number == null && raw['cpf'] != null) {
      out.document_number = String(raw['cpf']);
    }

    if (cd) {
      out.banking = {
        holder_name: String(
          cd['full_name'] ?? cd['holder_name'] ?? out.banking?.holder_name ?? ''
        ),
        bank_name:
          cd['bank_name'] != null ? String(cd['bank_name']) : out.banking?.bank_name,
        bank_code:
          cd['bank_code'] != null ? Number(cd['bank_code']) : out.banking?.bank_code,
        bank_branch:
          cd['bank_branch'] != null ? String(cd['bank_branch']) : out.banking?.bank_branch,
        account:
          cd['account'] != null ? String(cd['account']) : out.banking?.account,
        pix_key: cd['pix_key'] != null ? String(cd['pix_key']) : out.banking?.pix_key,
        pix_key_type:
          cd['pix_key_type'] != null
            ? String(cd['pix_key_type'])
            : out.banking?.pix_key_type,
        iban: (cd['iban'] as string | null | undefined) ?? out.banking?.iban,
      };
      if (cd['workflow_stage'] != null) {
        out.workflow_stage = String(cd['workflow_stage']);
      }
      if (cd['rejection_reason'] != null) {
        out.rejection_reason = String(cd['rejection_reason']);
      }
      if (cd['reviewed_by'] != null) {
        out.reviewed_by = String(cd['reviewed_by']);
      }
      if (cd['reviewed_at'] != null) {
        out.reviewed_at = cd['reviewed_at'] as string;
      }
      if (cd['paid_by'] != null) {
        out.paid_by = String(cd['paid_by']);
      }
      if (cd['paid_at'] != null) {
        out.paid_at = cd['paid_at'] as string;
      }
    }

    this.mergeDetailRiskAlerts(out, raw);
    this.applyListRiskFromApi(out as Transaction & Record<string, unknown>);

    if (typeof out.has_open_risk !== 'boolean' && typeof out.has_risk === 'boolean') {
      out.has_open_risk = out.has_risk;
    }

    delete out['user_data'];
    delete out['cashout_data'];
    return out as TransactionDetail;
  }

  getTransactionTypeFilterOptions(): { label: string; value: string }[] {
    return buildTransactionTypeFilterOptions();
  }
}
